/**
 * CRM Hygiene Scanner — deterministic detectors that DRAFT fixes, not just
 * flag booleans.
 *
 * The auditor's finding on the sentinels was that they only raise flags —
 * a human still has to figure out the fix. This scanner closes that gap:
 * every {@link HygieneFinding} carries a concrete `proposedFix` as an
 * {@link ActionDiff} (the same old→new trust mechanic the inline-edit
 * governance uses), so a finding is *already* a reviewable, reversible,
 * queueable change the moment it is detected.
 *
 * Five detectors, five quiet ways a CRM rots:
 *   - `stale-stage`       — an open deal has sat in its stage past the allowance;
 *   - `dead-deal`         — stuck 2× past the allowance AND gone silent;
 *   - `missing-next-step` — an open deal where nobody owns the next move;
 *   - `stale-touch`       — last recorded touch is older than the allowance;
 *   - `duplicate-contact` — fuzzy name/email/domain match between two people.
 *
 * Honesty rules (mirroring dealSentinel):
 *   - PURE + deterministic: `now` is injected, no store/firebase imports,
 *     same inputs → identical output including order.
 *   - Strict (`>`) boundary comparisons — a record exactly at the allowance
 *     is not yet stale; the scanner never rounds a record into trouble.
 *   - `proposedFix` is a *proposal shape*, never a write: callers route it
 *     through the drafter → bundle → approval pipeline.
 */

import { buildCellEditDiff, type ActionDiff } from './inlineEditDiff';

export type { ActionDiff };

const DAY_MS = 24 * 60 * 60 * 1000;

// ===========================================================================
// Types
// ===========================================================================

export type HygieneKind =
  | 'stale-stage'
  | 'dead-deal'
  | 'missing-next-step'
  | 'duplicate-contact'
  | 'stale-touch';

export type HygieneSeverity = 'low' | 'med' | 'high';

/** One detected hygiene problem, carrying its ready-to-review draft fix. */
export type HygieneFinding = {
  /** Deterministic id: `${kind}:${recordId}` (dupes: `${kind}:${a}:${b}`). */
  id: string;
  kind: HygieneKind;
  recordId: string;
  severity: HygieneSeverity;
  /** Human-readable justification lines — the WHY behind the fix. */
  evidence: string[];
  /** The concrete, reversible old→new fix this finding proposes. */
  proposedFix: ActionDiff;
};

/**
 * Minimal, adapter-friendly view of a person/prospect. Callers build this
 * from Person records — the scanner itself never touches the store.
 */
export interface HygienePerson {
  id: string;
  name: string;
  email?: string;
  company?: string;
  /** Open pipeline stage key ('new' | 'qualified' | 'proposal'). */
  stage?: string;
  /** Epoch ms when the record ENTERED its current stage (or last changed). */
  stageEnteredAt?: number;
  nextStep?: string;
  followUpDate?: string;
}

/** Minimal view of an account for the stage-age detector. */
export interface HygieneAccount {
  id: string;
  name: string;
  stage?: string;
  /** Epoch ms of the last stage movement / update. */
  stageEnteredAt?: number;
  nextStep?: string;
}

/** One touch (note / call / email / meeting) against a record. */
export interface TouchEvent {
  recordId: string;
  /** Epoch ms of the touch. */
  at: number;
}

export interface HygieneConfig {
  /** Days a stage / touch may age before it is flagged (strict `>`). */
  staleDays: number;
}

/** Stages the scanner treats as an OPEN deal on a person record. */
export const OPEN_STAGES: ReadonlySet<string> = new Set([
  'new',
  'qualified',
  'proposal',
]);

/** Minimum fuzzy score at which a pair becomes a duplicate-contact finding. */
export const DUPLICATE_THRESHOLD = 0.6;

// ===========================================================================
// Duplicate detection — dupeKey + fuzzy scoring
// ===========================================================================

/** Lowercase, strip punctuation/whitespace — the canonical name form. */
function normName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Email domain, lowercased ('' when absent/malformed). */
function domainOf(email: string | undefined): string {
  const at = (email ?? '').lastIndexOf('@');
  return at >= 0 ? (email as string).slice(at + 1).trim().toLowerCase() : '';
}

/**
 * Deterministic duplicate-bucket key: normalized name + email domain.
 * Two records of the same human at the same company collapse to one key
 * regardless of casing, punctuation or spacing.
 */
export function dupeKey(p: Pick<HygienePerson, 'name' | 'email'>): string {
  return `${normName(p.name)}|${domainOf(p.email)}`;
}

/**
 * Sørensen–Dice bigram similarity on normalized strings (0..1). A classic
 * public-domain algorithm, reimplemented here — no copied source.
 */
function diceSimilarity(a: string, b: string): number {
  if (a === b) return a.length > 0 ? 1 : 0;
  if (a.length < 2 || b.length < 2) return 0;
  const bigrams = new Map<string, number>();
  for (let i = 0; i < a.length - 1; i++) {
    const bg = a.slice(i, i + 2);
    bigrams.set(bg, (bigrams.get(bg) ?? 0) + 1);
  }
  let hits = 0;
  for (let i = 0; i < b.length - 1; i++) {
    const bg = b.slice(i, i + 2);
    const n = bigrams.get(bg) ?? 0;
    if (n > 0) {
      hits++;
      bigrams.set(bg, n - 1);
    }
  }
  return (2 * hits) / (a.length - 1 + (b.length - 1));
}

/** One scored candidate duplicate pair (a/b are record ids, a first-seen). */
export interface DuplicatePair {
  a: string;
  b: string;
  /** 0..1 fused name/email/domain similarity, rounded to 2dp. */
  score: number;
}

/**
 * Score every person pair on fused name/email/domain similarity:
 *
 *   score = min(1, 0.55·nameSim + 0.45·emailExact + 0.15·domainMatch)
 *
 * Pairs scoring below `minScore` (default 0.5) are dropped as noise; the
 * scanner itself only converts pairs at/above {@link DUPLICATE_THRESHOLD}
 * into findings. Deterministic: sorted score-desc, then by ids.
 */
export function fuzzyDuplicates(
  people: HygienePerson[],
  minScore = 0.5,
): DuplicatePair[] {
  const out: DuplicatePair[] = [];
  for (let i = 0; i < people.length; i++) {
    for (let j = i + 1; j < people.length; j++) {
      const a = people[i];
      const b = people[j];
      const nameSim = diceSimilarity(normName(a.name), normName(b.name));
      const emailA = (a.email ?? '').trim().toLowerCase();
      const emailB = (b.email ?? '').trim().toLowerCase();
      const emailExact = emailA !== '' && emailA === emailB ? 1 : 0;
      const domA = domainOf(a.email);
      const domB = domainOf(b.email);
      const domainMatch = domA !== '' && domA === domB ? 1 : 0;
      const raw = 0.55 * nameSim + 0.45 * emailExact + 0.15 * domainMatch;
      const score = Math.round(Math.min(1, raw) * 100) / 100;
      if (score >= minScore) out.push({ a: a.id, b: b.id, score });
    }
  }
  return out.sort(
    (x, y) => y.score - x.score || x.a.localeCompare(y.a) || x.b.localeCompare(y.b),
  );
}

// ===========================================================================
// Scan
// ===========================================================================

/** Whole days between two epoch-ms instants, floored. Never negative. */
function daysBetween(earlier: number, later: number): number {
  return Math.max(0, Math.floor((later - earlier) / DAY_MS));
}

/** YYYY-MM-DD (UTC) for `now + days` — the deterministic follow-up date. */
function isoDatePlus(now: number, days: number): string {
  return new Date(now + days * DAY_MS).toISOString().slice(0, 10);
}

function fix(
  recordId: string,
  attr: string,
  from: unknown,
  to: unknown,
): ActionDiff {
  return buildCellEditDiff({ recordId, attr, from: from ?? null, to });
}

/**
 * Run every deterministic detector over the CRM snapshot. Pure: same
 * inputs + same `now` → identical findings in identical order (people in
 * input order with a fixed per-record kind order, then accounts, then
 * duplicate pairs by descending score).
 */
export function scanHygiene(
  accounts: HygieneAccount[],
  people: HygienePerson[],
  timelines: TouchEvent[],
  now: number,
  cfg: HygieneConfig,
): HygieneFinding[] {
  const staleMs = cfg.staleDays * DAY_MS;

  // Index the latest touch per record once.
  const lastTouch = new Map<string, number>();
  for (const t of timelines) {
    const prev = lastTouch.get(t.recordId);
    if (prev === undefined || t.at > prev) lastTouch.set(t.recordId, t.at);
  }

  const findings: HygieneFinding[] = [];

  for (const p of people) {
    const open = p.stage !== undefined && OPEN_STAGES.has(p.stage);
    const touch = lastTouch.get(p.id);

    // 1) stale-stage / dead-deal — an open deal aging past the allowance.
    if (open && p.stageEnteredAt !== undefined) {
      const ageMs = now - p.stageEnteredAt;
      if (ageMs > staleMs) {
        const ageDays = daysBetween(p.stageEnteredAt, now);
        const quietSince = Math.max(touch ?? -Infinity, p.stageEnteredAt);
        const silent = now - quietSince > staleMs;
        if (ageMs > 2 * staleMs && silent) {
          // Stuck twice past the allowance AND gone silent: propose closing
          // it as lost — the honest state — rather than letting it rot open.
          findings.push({
            id: `dead-deal:${p.id}`,
            kind: 'dead-deal',
            recordId: p.id,
            severity: 'high',
            evidence: [
              `"${p.name}" has sat in ${p.stage} for ${ageDays} days (allowance ${cfg.staleDays}).`,
              `No touch in ${daysBetween(quietSince, now)} days — the deal has gone silent.`,
            ],
            proposedFix: fix(p.id, 'stage', p.stage, 'closed-lost'),
          });
        } else {
          findings.push({
            id: `stale-stage:${p.id}`,
            kind: 'stale-stage',
            recordId: p.id,
            severity: ageMs > 1.5 * staleMs ? 'med' : 'low',
            evidence: [
              `"${p.name}" has sat in ${p.stage} for ${ageDays} days (allowance ${cfg.staleDays}).`,
            ],
            proposedFix: fix(
              p.id,
              'nextStep',
              p.nextStep,
              `Stage review: stuck in ${p.stage} for ${ageDays}d — advance or close`,
            ),
          });
        }
      }
    }

    // 2) missing-next-step — an open deal where nobody owns the next move.
    if (open && !p.nextStep && !p.followUpDate) {
      findings.push({
        id: `missing-next-step:${p.id}`,
        kind: 'missing-next-step',
        recordId: p.id,
        severity: 'med',
        evidence: [`"${p.name}" is an open ${p.stage} deal with no next step or follow-up date.`],
        proposedFix: fix(
          p.id,
          'nextStep',
          p.nextStep,
          `Set next step — no owner for the next move on this ${p.stage} deal`,
        ),
      });
    }

    // 3) stale-touch — the FRESHEST provable activity (latest touch or the
    //    stage-entry instant, whichever is newer) is older than the
    //    allowance. A record with neither signal cannot be proven stale,
    //    so the scanner honestly stays silent about it.
    const provable = [touch, p.stageEnteredAt].filter(
      (t): t is number => t !== undefined,
    );
    const lastProvable = provable.length > 0 ? Math.max(...provable) : undefined;
    if (lastProvable !== undefined && now - lastProvable > staleMs) {
      const quietDays = daysBetween(lastProvable, now);
      findings.push({
        id: `stale-touch:${p.id}`,
        kind: 'stale-touch',
        recordId: p.id,
        severity: quietDays > 2 * cfg.staleDays ? 'med' : 'low',
        evidence: [
          `"${p.name}" last touched ${quietDays} days ago (allowance ${cfg.staleDays}).`,
        ],
        proposedFix: fix(p.id, 'followUpDate', p.followUpDate, isoDatePlus(now, 3)),
      });
    }
  }

  // 4) Account stage age — mirrors the person detector at account grain.
  for (const a of accounts) {
    if (a.stage === undefined || a.stageEnteredAt === undefined) continue;
    const ageMs = now - a.stageEnteredAt;
    if (ageMs > staleMs) {
      const ageDays = daysBetween(a.stageEnteredAt, now);
      findings.push({
        id: `stale-stage:${a.id}`,
        kind: 'stale-stage',
        recordId: a.id,
        severity: ageMs > 2 * staleMs ? 'med' : 'low',
        evidence: [
          `Account "${a.name}" has sat in ${a.stage} for ${ageDays} days (allowance ${cfg.staleDays}).`,
        ],
        proposedFix: fix(
          a.id,
          'nextStep',
          a.nextStep,
          `Stage review: "${a.name}" in ${a.stage} for ${ageDays}d — advance or revisit`,
        ),
      });
    }
  }

  // 5) duplicate-contact — fuzzy pairs at/above the threshold. The fix
  //    marks the LATER record (b) as a duplicate of the first-seen (a):
  //    a reviewable pointer, never a destructive auto-merge.
  const byId = new Map(people.map((p) => [p.id, p]));
  for (const pair of fuzzyDuplicates(people)) {
    if (pair.score < DUPLICATE_THRESHOLD) continue;
    const a = byId.get(pair.a);
    const b = byId.get(pair.b);
    if (!a || !b) continue;
    findings.push({
      id: `duplicate-contact:${pair.a}:${pair.b}`,
      kind: 'duplicate-contact',
      recordId: pair.b,
      severity: pair.score >= 0.85 ? 'high' : 'med',
      evidence: [
        `"${b.name}" <${b.email ?? '—'}> looks like a duplicate of "${a.name}" <${a.email ?? '—'}> (similarity ${pair.score}).`,
        `Shared key: ${dupeKey(a)} ~ ${dupeKey(b)}.`,
      ],
      proposedFix: fix(pair.b, 'duplicateOf', null, pair.a),
    });
  }

  return findings;
}
